/**
 * Authentication API endpoints
 */

import { apiClient } from './client';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  phone?: string;
  role: 'tourist' | 'operator';
}

export interface AuthUserPayload {
  id: string;
  email: string;
  name: string;
  role?: string;
  displayName?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  isOperator?: boolean;
  isTourist?: boolean;
  status?: 'INACTIVE' | 'ACTIVE' | 'DELETED';
  emailVerified?: boolean;
  phoneVerified?: boolean;
  isOperatorVerified?: boolean;
  hadAddedBoats?: boolean;
}

export interface AuthResponse {
  user: AuthUserPayload;
  accessToken: string;
  refreshToken: string;
}

export interface EmailCheckResponse {
  exists: boolean;
  user: {
    isOperator: boolean;
    isTourist: boolean;
    status: 'INACTIVE' | 'ACTIVE' | 'DELETED';
  } | null;
}

export const authApi = {
  /**
   * Register a new user
   */
  async register(data: RegisterData) {
    return apiClient.post<AuthResponse>('/auth/register', data);
  },

  /**
   * Login user
   */
  async login(credentials: LoginCredentials) {
    const response = await apiClient.post<AuthResponse>('/auth/login', credentials);
    if (response.success && response.data) {
      apiClient.setToken(response.data.accessToken);
      apiClient.setRefreshToken(response.data.refreshToken);
    }
    return response;
  },

  /**
   * Logout user
   */
  async logout(refreshToken?: string) {
    const token = refreshToken || apiClient.getRefreshToken();
    if (token) {
      await apiClient.post('/auth/logout', { refreshToken: token });
    }
    apiClient.removeToken();
  },

  /**
   * Refresh access token
   */
  async refresh(refreshToken?: string) {
    const token = refreshToken || apiClient.getRefreshToken();
    if (!token) {
      return {
        success: false,
        error: {
          code: 'NO_REFRESH_TOKEN',
          message: 'No refresh token available',
        },
      };
    }

    const response = await apiClient.post<{ accessToken: string; refreshToken: string }>(
      '/auth/refresh',
      { refreshToken: token }
    );

    if (response.success && response.data) {
      apiClient.setToken(response.data.accessToken);
      apiClient.setRefreshToken(response.data.refreshToken);
    }

    return response;
  },

  /**
   * Get current user profile
   */
  async getCurrentUser() {
    const response = await apiClient.get('/users/me');
    return response;
  },

  async checkEmail(email: string) {
    return apiClient.post<EmailCheckResponse>('/auth/check-email', {
      email: email.trim().toLowerCase(),
    });
  },

  async getGoogleClientConfig() {
    return apiClient.get<{ clientId: string }>('/auth/google/client-config');
  },

  async authenticateWithGoogle(role: 'tourist' | 'operator', credential: string) {
    const response = await apiClient.post<AuthResponse>('/auth/google', {
      role,
      credential,
    });
    if (response.success && response.data) {
      apiClient.setToken(response.data.accessToken);
      apiClient.setRefreshToken(response.data.refreshToken);
    }
    return response;
  },

  async updateCurrentUser(data: {
    name: string;
    email: string;
    phone?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postalCode?: string;
    };
  }) {
    return apiClient.patch('/users/me', data);
  },

  async enableOperatorMode() {
    const response = await apiClient.post<AuthResponse>('/users/me/enable-operator');
    if (response.success && response.data) {
      apiClient.setToken(response.data.accessToken);
      apiClient.setRefreshToken(response.data.refreshToken);
    }
    return response;
  },

  /**
   * Send OTP to operator email
   */
  async sendOperatorOTP(email: string, type: 'LOGIN' | 'SIGNUP', isDummyMode: boolean = false) {
    // Pass dummy mode as query parameter
    const endpoint = isDummyMode 
      ? `/auth/operator/send-otp?isDummyMode=true`
      : '/auth/operator/send-otp';
    return apiClient.post(endpoint, { email, type });
  },

  /**
   * Verify OTP and login/signup operator
   */
  async verifyOperatorOTP(
    email: string,
    code: string,
    type: 'LOGIN' | 'SIGNUP',
    name?: string,
    isDummyMode: boolean = false
  ) {
    // Pass dummy mode as query parameter
    const endpoint = isDummyMode
      ? `/auth/operator/verify-otp?isDummyMode=true`
      : '/auth/operator/verify-otp';
    const response = await apiClient.post<AuthResponse>(endpoint, {
      email,
      code,
      type,
      name,
    });
    if (response.success && response.data) {
      apiClient.setToken(response.data.accessToken);
      apiClient.setRefreshToken(response.data.refreshToken);
    }
    return response;
  },

  /**
   * Complete operator profile (for authenticated users)
   */
  async completeOperatorProfile(
    email: string,
    payload: {
      name: string;
      phone: string;
      address: {
        line1: string;
        line2?: string;
        city: string;
        state: string;
        postalCode: string;
      };
    }
  ) {
    const response = await apiClient.post<{ user: any }>('/auth/operator/complete-profile', {
      email,
      ...payload,
    });
    return response;
  },

  /**
   * Send OTP to tourist email for login
   */
  async sendTouristOTP(email: string, isDummyMode: boolean = false, type: 'LOGIN' | 'SIGNUP' = 'LOGIN') {
    const endpoint = isDummyMode 
      ? `/auth/tourist/send-otp?isDummyMode=true`
      : '/auth/tourist/send-otp';
    return apiClient.post(endpoint, { email, type });
  },

  /**
   * Verify OTP and login/create tourist user
   */
  async loginTourist(email: string, code: string, isDummyMode: boolean = false, type: 'LOGIN' | 'SIGNUP' = 'LOGIN') {
    const endpoint = isDummyMode
      ? `/auth/tourist/verify-otp?isDummyMode=true`
      : '/auth/tourist/verify-otp';
    const response = await apiClient.post<AuthResponse>(endpoint, {
      email,
      code,
      type,
    });
    if (response.success && response.data) {
      apiClient.setToken(response.data.accessToken);
      apiClient.setRefreshToken(response.data.refreshToken);
    }
    return response;
  },
};
